// Single source of truth for editable site content + theme.
// Stored as one JSONB row in `site_settings` and a `cars` table.
//
// Localization: every user-facing text field has an optional `_en` companion.
// When the EN field is empty, the UI falls back to the RU value.

export type ThemeSettings = {
  // HSL strings like "217 100% 50%"
  background: string;
  foreground: string;
  card: string;
  primary: string;
  primaryForeground: string;
  border: string;
  // Font family (CSS value)
  fontHeading: string;
  fontBody: string;
};

export type HeroContent = {
  title: string;
  title_en?: string;
  subtitle: string;
  subtitle_en?: string;
  ctaLabel: string;
  ctaLabel_en?: string;
  imageUrl: string | null;
  features: string[]; // 4 items
  features_en?: string[]; // optional 4 items
};

export type AboutContent = {
  eyebrow: string;
  eyebrow_en?: string;
  title: string;
  title_en?: string;
  paragraph1: string;
  paragraph1_en?: string;
  paragraph2: string;
  paragraph2_en?: string;
  imageUrl: string | null;
};

export type ProcessContent = {
  title: string;
  title_en?: string;
  steps: string[]; // 4 items
  steps_en?: string[];
  imageUrl: string | null;
};

export type CategoryLabels = { econom: string; comfort: string; business: string; premium: string };

export type CatalogContent = {
  title: string;
  title_en?: string;
  description: string;
  description_en?: string;
  categoryLabels: CategoryLabels;
  categoryLabels_en?: Partial<CategoryLabels>;
};

export type FaqItem = { q: string; a: string; q_en?: string; a_en?: string };
export type FaqContent = {
  title: string;
  title_en?: string;
  description: string;
  description_en?: string;
  items: FaqItem[];
};

export type ConsultContent = {
  title: string;
  title_en?: string;
  description: string;
  description_en?: string;
  bookingUrl: string; // WhatsApp/Telegram link
  imageUrl: string | null;
  imageUrl2: string | null; // banner for the SECOND consultation block
  submitLabel: string;
  submitLabel_en?: string;
  namePlaceholder: string;
  namePlaceholder_en?: string;
  phonePlaceholder: string;
  phonePlaceholder_en?: string;
  buttonColor: string; // HSL "h s% l%"
};

export type FooterContact = {
  type: "instagram" | "whatsapp" | "phone" | "address" | "email" | "website";
  label: string; // e.g. "Instagram", "WhatsApp", "Телефон"
  label_en?: string;
  value: string; // displayed value
  value_en?: string;
  url?: string; // optional link
};

export type FooterContent = {
  copyright: string;
  copyright_en?: string;
  links: { label: string; label_en?: string; url: string }[];
  contacts: FooterContact[];
};

export type SiteSettings = {
  theme: ThemeSettings;
  hero: HeroContent;
  about: AboutContent;
  catalog: CatalogContent;
  process: ProcessContent;
  consult: ConsultContent;
  faq: FaqContent;
  footer: FooterContent;
};

export const DEFAULT_SETTINGS: SiteSettings = {
  theme: {
    background: "0 0% 4%",
    foreground: "0 0% 98%",
    card: "0 0% 7%",
    primary: "217 100% 50%",
    primaryForeground: "0 0% 100%",
    border: "0 0% 16%",
    fontHeading: "Inter, system-ui, sans-serif",
    fontBody: "Inter, system-ui, sans-serif",
  },
  hero: {
    title: "Аренда авто",
    title_en: "Car rental",
    subtitle: "Авто от эконом до премиум класса. Быстрое оформление. Минимум документов.",
    subtitle_en: "Cars from economy to premium class. Fast paperwork. Minimum documents.",
    ctaLabel: "Забронировать авто",
    ctaLabel_en: "Book a car",
    imageUrl: null,
    features: [
      "Свободу передвижения,\nэкономию времени и денег",
      "Автомобили разных\nклассов",
      "Возможность аренды с\nводителем",
      "Минимум документов,\nбыстрое оформление",
    ],
    features_en: [
      "Freedom of movement,\nsaving time and money",
      "Cars of different\nclasses",
      "Option to rent\nwith a driver",
      "Minimum paperwork,\nfast process",
    ],
  },
  about: {
    eyebrow: "О НАС",
    eyebrow_en: "ABOUT US",
    title: "Комфорт, стиль и свобода передвижения",
    title_en: "Comfort, style and freedom of movement",
    paragraph1:
      "Мы — компания по аренде автомобилей, созданная для тех, кто ценит комфорт, стиль и свободу передвижения. Мы предоставляем современные, ухоженные автомобили в отличном техническом состоянии — от бизнес-класса до премиальных моделей.",
    paragraph1_en:
      "We are a car rental company built for those who value comfort, style and freedom of movement. We offer modern, well-maintained cars in excellent technical condition — from business class to premium models.",
    paragraph2:
      "Наша цель — сделать аренду автомобиля простой, прозрачной и удобной. Мы предлагаем гибкие условия аренды, индивидуальный подход к каждому клиенту и высокий уровень сервиса на всех этапах — от подбора авто до его возврата.",
    paragraph2_en:
      "Our goal is to make car rental simple, transparent and convenient. We offer flexible rental terms, an individual approach to every client and a high level of service at every step — from picking the car to returning it.",
    imageUrl: null,
  },
  catalog: {
    title: "КАТАЛОГ АВТОМОБИЛЕЙ",
    title_en: "CAR CATALOG",
    description:
      "От доступных городских моделей до автомобилей представительского класса — подберите свой вариант: Эконом / Комфорт / Бизнес / Премиум.",
    description_en:
      "From affordable city models to executive-class cars — find your option: Economy / Comfort / Business / Premium.",
    categoryLabels: { econom: "Эконом", comfort: "Комфорт", business: "Бизнес", premium: "Премиум" },
    categoryLabels_en: { econom: "Economy", comfort: "Comfort", business: "Business", premium: "Premium" },
  },
  process: {
    title: "КАК ПРОХОДИТ АРЕНДА:",
    title_en: "HOW THE RENTAL WORKS:",
    steps: [
      "Вы выбираете\nавтомобиль",
      "Оставляете заявку или\nпишете в мессенджер",
      "Подтверждаете бронь",
      "Забираете автомобиль\nили заказываете доставку",
    ],
    steps_en: [
      "You pick\na car",
      "Submit a request or\nmessage us",
      "Confirm the booking",
      "Pick up the car\nor request delivery",
    ],
    imageUrl: null,
  },
  consult: {
    title: "БЕСПЛАТНАЯ КОНСУЛЬТАЦИЯ",
    title_en: "FREE CONSULTATION",
    description: "Заполните анкету и получите бесплатную консультацию",
    description_en: "Fill in the form and get a free consultation",
    bookingUrl: "https://wa.me/996508810010",
    imageUrl: null,
    imageUrl2: null,
    submitLabel: "Отправить",
    submitLabel_en: "Send",
    namePlaceholder: "ФИО",
    namePlaceholder_en: "Full name",
    phonePlaceholder: "Номер телефона",
    phonePlaceholder_en: "Phone number",
    buttonColor: "211 100% 50%",
  },
  faq: {
    title: "ЧАСТО ЗАДАВАЕМЫЕ ВОПРОСЫ",
    title_en: "FREQUENTLY ASKED QUESTIONS",
    description: "Ниже прописаны вопросы от клиентов и ответы от Freeways.kg",
    description_en: "Below are questions from clients and answers from Freeways.kg",
    items: [
      {
        q: "Какая сумма залога?",
        a: "Сумма залога начинается от 300$ в зависимости от машины.",
        q_en: "What is the deposit amount?",
        a_en: "The deposit starts from $300 depending on the car.",
      },
      {
        q: "Есть ли страховка?",
        a: "Да, на всех машинах имеется страховка.",
        q_en: "Is insurance included?",
        a_en: "Yes, all cars have insurance.",
      },
      {
        q: "Обслужена ли машина?",
        a: "Наши машины проходят своевременное ТО.",
        q_en: "Is the car serviced?",
        a_en: "Our cars receive timely maintenance.",
      },
      {
        q: "Какой суточный лимит?",
        a: "Суточный лимит пробега по городу 250 км, по трассе 300 км. Перепробег считаем по 30с за км.",
        q_en: "What is the daily mileage limit?",
        a_en: "Daily mileage limit is 250 km in the city and 300 km on highways. Excess mileage is charged at 30¢ per km.",
      },
      {
        q: "Ограничения скорости?",
        a: "По городу 80 км/ч, по трассе 120 км/ч.",
        q_en: "Are there speed limits?",
        a_en: "80 km/h in the city, 120 km/h on highways.",
      },
      {
        q: "Возврат суммы залога",
        a: "По окончанию аренды удерживаем 100$ на 21 день на проверку штрафов, остальная сумма возвращается сразу.",
        q_en: "Deposit refund",
        a_en: "After the rental ends we hold $100 for 21 days to check for fines; the rest is refunded immediately.",
      },
      {
        q: "Нет ли скрытых платежей?",
        a: "Скрытых платежей нет, всё прозрачно — составляем двухсторонний договор.",
        q_en: "Are there any hidden fees?",
        a_en: "No hidden fees — everything is transparent and a two-sided contract is signed.",
      },
      {
        q: "От скольки $ начинается аренда?",
        a: "От 35$ в сутки.",
        q_en: "What is the starting price?",
        a_en: "From $35 per day.",
      },
      {
        q: "Есть ли скидки на длительный период?",
        a: "Скидки есть от 5 дней и от 10 дней. Всё что больше — обговариваем индивидуально.",
        q_en: "Are there discounts for long rentals?",
        a_en: "Yes, discounts apply from 5 days and from 10 days. Anything longer is negotiated individually.",
      },
      {
        q: "Выдаёте ли чеки и квитанции?",
        a: "Выдаём все чеки и квитанции, сотрудничаем с организациями.",
        q_en: "Do you issue receipts and invoices?",
        a_en: "Yes, we issue all receipts and invoices and work with companies.",
      },
      {
        q: "Какие методы оплаты?",
        a: "Можно оплачивать картой, QR-кодом, принимаем оплату в любой валюте, также USDT.",
        q_en: "What payment methods do you accept?",
        a_en: "You can pay by card, QR code, in any currency, and we also accept USDT.",
      },
      {
        q: "Есть ли доставка?",
        a: "Доставка есть по городу, в аэропорт, а также в любую точку Кыргызстана.",
        q_en: "Do you offer delivery?",
        a_en: "Yes, we deliver within the city, to the airport and to any place in Kyrgyzstan.",
      },
    ],
  },
  footer: {
    copyright: "Freeways.kg — Аренда авто в Бишкеке",
    copyright_en: "Freeways.kg — Car rental in Bishkek",
    links: [
      { label: "Instagram", label_en: "Instagram", url: "https://instagram.com/freeways.kg" },
      { label: "WhatsApp", label_en: "WhatsApp", url: "https://wa.me/996508810010" },
      { label: "Telegram", label_en: "Telegram", url: "#" },
    ],
    contacts: [
      { type: "instagram", label: "Instagram", label_en: "Instagram", value: "@freeways.kg", url: "https://instagram.com/freeways.kg" },
      { type: "whatsapp", label: "WhatsApp", label_en: "WhatsApp", value: "+996 508 810 010", url: "https://wa.me/996508810010" },
      { type: "phone", label: "Телефон", label_en: "Phone", value: "+996 508 810 010", url: "tel:+996508810010" },
      { type: "phone", label: "Телефон", label_en: "Phone", value: "+996 778 810 010", url: "tel:+996778810010" },
      { type: "address", label: "Адрес", label_en: "Address", value: "Бишкек, ул. Султана Ибраимова, 115/4", value_en: "Bishkek, Sultan Ibraimov street, 115/4" },
      { type: "email", label: "Эл. почта", label_en: "Email", value: "user00@gmail.com", url: "mailto:user00@gmail.com" },
      { type: "website", label: "Сайт", label_en: "Website", value: "freeways.kg", url: "https://freeways.kg" },
    ],
  },
};

// Deep-merge for nested partial overrides loaded from DB.
export function mergeSettings(
  base: SiteSettings,
  override: Partial<SiteSettings> | null | undefined,
): SiteSettings {
  if (!override) return base;
  const out: any = { ...base };
  for (const key of Object.keys(override)) {
    const k = key as keyof SiteSettings;
    const baseVal = (base as any)[k];
    const overrideVal = (override as any)[k];
    if (
      overrideVal &&
      typeof overrideVal === "object" &&
      !Array.isArray(overrideVal) &&
      baseVal &&
      typeof baseVal === "object" &&
      !Array.isArray(baseVal)
    ) {
      out[k] = { ...baseVal, ...overrideVal };
    } else if (overrideVal !== undefined) {
      out[k] = overrideVal;
    }
  }
  return out;
}

// Car DB row
export type CarRow = {
  id: string;
  name: string;
  name_en: string;
  category: "econom" | "comfort" | "business" | "premium";
  description: string;
  description_en: string;
  price_short: string;
  price_mid: string;
  price_long: string;
  price_label: string;
  price_label_en: string;
  image_url: string | null;
  gallery: string[];
  sort_order: number;
  is_active: boolean;
};

// Build a RU→EN dictionary from DEFAULT_SETTINGS so that user-edited content
// in the DB which lacks _en fields can still fall back to a sensible English
// value when the RU text matches a known default.
export function buildDefaultRuEnDict(): Record<string, string> {
  const dict: Record<string, string> = {};
  const add = (ru?: string | null, en?: string | null) => {
    if (!ru || !en) return;
    const k = ru.trim();
    const v = en.trim();
    if (k && v) dict[k] = v;
  };

  const d = DEFAULT_SETTINGS;
  add(d.hero.title, d.hero.title_en);
  add(d.hero.subtitle, d.hero.subtitle_en);
  add(d.hero.ctaLabel, d.hero.ctaLabel_en);
  d.hero.features.forEach((f, i) => add(f, d.hero.features_en?.[i]));

  add(d.about.eyebrow, d.about.eyebrow_en);
  add(d.about.title, d.about.title_en);
  add(d.about.paragraph1, d.about.paragraph1_en);
  add(d.about.paragraph2, d.about.paragraph2_en);

  add(d.catalog.title, d.catalog.title_en);
  add(d.catalog.description, d.catalog.description_en);
  (Object.keys(d.catalog.categoryLabels) as Array<keyof CategoryLabels>).forEach((k) =>
    add(d.catalog.categoryLabels[k], d.catalog.categoryLabels_en?.[k]),
  );

  add(d.process.title, d.process.title_en);
  d.process.steps.forEach((s, i) => add(s, d.process.steps_en?.[i]));

  add(d.consult.title, d.consult.title_en);
  add(d.consult.description, d.consult.description_en);
  add(d.consult.submitLabel, d.consult.submitLabel_en);
  add(d.consult.namePlaceholder, d.consult.namePlaceholder_en);
  add(d.consult.phonePlaceholder, d.consult.phonePlaceholder_en);

  add(d.faq.title, d.faq.title_en);
  add(d.faq.description, d.faq.description_en);
  d.faq.items.forEach((it) => {
    add(it.q, it.q_en);
    add(it.a, it.a_en);
  });

  add(d.footer.copyright, d.footer.copyright_en);
  d.footer.links.forEach((l) => add(l.label, l.label_en));
  d.footer.contacts.forEach((c) => {
    add(c.label, c.label_en);
    add(c.value, c.value_en);
  });

  return dict;
}

export const DEFAULT_RU_EN_DICT: Record<string, string> = buildDefaultRuEnDict();

